"use client";

import { useEffect, useMemo, useState } from "react";
import { toast_configs } from "@core/constants/toast_configs";
import useFormRenderer from "@core/helpers/client/hooks/useFormRenderer";
import { openAuthModal } from "@/hooks/useAuthModal";
import ModalComponent from "@/components/ui/ModalComponent";
import { SITE_FIELD_COMPONENTS } from "@/components/common/formBuilder/siteFields";
import { SITE_MESSAGES } from "@/components/common/formBuilder/siteChecks";
import { WIDGET_FORM_CLASS_NAMES } from "@/components/common/formBuilder/WidgetForm";

/**
 * گزارش محصول فرمی از فرم‌ساز پنل است با کلید `report-product` (نیاز به ورود دارد). فیلدها (مشکل‌های محصول و توضیحات)، بررسی‌ها و
 * ثبت پاسخ با core است؛ این‌جا فقط مودال و دکمه‌ی ثبت در فوتر آن است. پاسخ با مدل صفحه (model_name و model_type)، شناسه‌ی
 * محصول و کلیدهای تنوعِ انتخاب‌شده (`extra_meta`) ثبت می‌شود تا پنل در نمای پاسخ کارت همان محصول و لینک ویرایش همان نوعش را نشان بدهد.
 */
export const REPORT_PRODUCT_FORM_KEY = "report-product";

const FORM_ID = "report-product-form";

const toast = (message, type) =>
    toast_configs(message, type, { className: "custome_toastify", position: "bottom-center", autoClose: 4000 });

const open_login = () => openAuthModal();

function ReportForm({ report_model, onSuccess, onBusyChange }) {
    const { model_name, model_type, model_id, variant_keys } = report_model || {};
    // مدل صفحه، شناسه‌ی محصول و تنوع فقط وقتی مقدار دارند فرستاده می‌شوند (core مقدار خالی را رد می‌کند)
    const extra_meta = useMemo(() => {
        const extra = {};
        if (model_name) extra.model_name = String(model_name);
        if (model_type !== undefined && model_type !== null && model_type !== "") extra.model_type = model_type;
        if (model_id) extra.model_id = String(model_id);
        if (variant_keys) extra.variant_keys = String(variant_keys);
        return extra;
    }, [model_name, model_type, model_id, variant_keys]);

    const form = useFormRenderer({
        form_key: REPORT_PRODUCT_FORM_KEY,
        extra_meta,
        field_components: SITE_FIELD_COMPONENTS,
        messages: SITE_MESSAGES,
        class_names: WIDGET_FORM_CLASS_NAMES,
        stable_class_names: true,
        scroll_to_error: true,
        // فایل‌ها هنگام ثبت فرم، جدا و با کپچا به سرور می‌روند (نه base64 داخل پاسخ)
        upload_files: true,
        on_login_required: open_login,
    });

    const group_keys = useMemo(() => (form.definition?.version?.groups || []).map((group) => group?.key), [form.definition]);

    useEffect(() => {
        onBusyChange?.(form.submitting);
    }, [form.submitting, onBusyChange]);

    if (form.not_found) {
        return <p className="py-6 text-center text-sm text-jmt-neutral-500">فرم گزارش در دسترس نیست.</p>;
    }
    if (!form.definition) {
        return <p className="py-6 text-center text-sm text-jmt-neutral-500">در حال بارگذاری…</p>;
    }

    const submit = async (event) => {
        event.preventDefault();
        if (form.submitting) return;
        const result = await form.submit();
        if (result?.status === "success") {
            toast("گزارش شما ثبت شد؛ ممنون که ما را در بهتر شدن کمک می‌کنید.", "success");
            form.reset();
            onSuccess?.();
        } else if (result?.status === "invalid") {
            toast("لطفا مقادیر را به درستی وارد کنید.", "error");
        }
    };

    return (
        // ۹۶٪ و وسط‌چین تا رینگ فوکوس فیلدها از دو طرف به لبه‌ی مودال نچسبد
        <form id={FORM_ID} onSubmit={submit} className="mx-auto flex w-[96%] flex-col gap-1" noValidate>
            {form.render_groups(group_keys)}
            {form.captcha_node}
            {form.form_error && (
                <p role="alert" className="text-xs text-jmt-red-600">
                    {form.form_error}
                </p>
            )}
        </form>
    );
}

/**
 * مودال «گزارش مشخصات محصول یا موارد قانونی». `report_model` = `{ model_name, model_type, model_id, variant_keys }` (مدل صفحه،
 * شناسه‌ی محصول و کلیدهای تنوع انتخاب‌شده با ویرگول) همراه پاسخ می‌رود.
 */
export default function ReportProductModal({ open, onClose, report_model }) {
    const [submitting, set_submitting] = useState(false);

    // دکمه‌ی ثبت در فوتر خودِ مودال است؛ فرم با شناسه‌اش submit می‌شود و فیلدها بالای فوتر اسکرول می‌شوند
    const footer = (
        <div className="w-full border-t border-jmt-neutral-200 bg-jmt-white pt-3">
            <button
                type="submit"
                form={FORM_ID}
                disabled={submitting}
                className="flex h-12 w-full cursor-pointer items-center justify-center rounded bg-jmt-neutral-950 px-4 py-2 text-sm font-bold leading-[22px] text-jmt-text-dark-mode transition hover:bg-jmt-neutral-800 disabled:cursor-default disabled:opacity-60">
                {submitting ? "در حال ارسال…" : "ارسال گزارش"}
            </button>
        </div>
    );

    return (
        <ModalComponent
            isOpen={open}
            onClose={onClose}
            title="گزارش مشخصات محصول یا موارد قانونی"
            size="sm"
            full
            className="h-[70vh]! max-h-[70vh]!"
            footer={footer}
            custom_footer={footer}>
            {/* فرم فقط وقتی مودال باز است ساخته می‌شود تا هر بار از اول و بدون پاسخ قبلی باز شود */}
            {open && <ReportForm report_model={report_model} onSuccess={onClose} onBusyChange={set_submitting} />}
        </ModalComponent>
    );
}
